import { AxiosResponse, CancelToken } from 'axios';
import { User } from '../../utils/AccountsAPI';
import { CreatedUpdatedTable } from '../../utils/APIReturnTypes';
import AxiosConfig from '../../utils/AxiosConfig';
import { CostCode } from '../../utils/CYMAAPI';
import { getUTCAmericanDateString, getUTCDateString } from '../../utils/Functions';
import { FileUploads } from '../files/FileAPI';

const axiosInstance = AxiosConfig.instance();

/*-------------------------------Expenses-------------------------------------*/
export function getExpensesById(id: number, token?: CancelToken): Promise<ExpenseReportHeader> {
  const url = '/expenses/' + id;

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function getExpensesForWeekEnding(userId: string, weekEndingDate: string, token?: CancelToken): Promise<ExpenseReportHeader> {
  const url = '/accounts/' + userId + '/expenses/' + weekEndingDate;

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function getExpensesForJob(jobNumber: string, weekEndingDate?: string, token?: CancelToken): Promise<ExpenseReportDetail[]> {
  const url = `cyma/jobs/${jobNumber}/expenses`;

  var config: any = { params: {} };
  if (token !== undefined) {
    config.cancelToken = token;
  }
  if (weekEndingDate !== undefined) config.params.weekEnding = getUTCDateString(weekEndingDate, 'yyyy-mm-dd');

  return axiosInstance.get(url, config).then((response) =>
    (response.data as ExpenseReportHeader[]).flatMap((header) => {
      return header.details.map((detail) => {
        detail.header = header;
        return detail;
      });
    })
  );
}

export function postExpenses(header: ExpenseReportHeader): Promise<ExpenseReportHeader> {
  const url = '/expenses';

  return axiosInstance.post(url, header).then((response) => response.data);
}

export function putExpenses(header: ExpenseReportHeader): Promise<ExpenseReportHeader> {
  const url = '/expenses/' + header.id;

  return axiosInstance.put(url, header).then((response) => response.data);
}

export function putExpensesApproval(userId: string, type: 'manager' | 'accounting', approved: boolean): Promise<ExpenseReportHeader> {
  const url = `/expenses/${userId}/approvals/${type}/${approved}`;

  return axiosInstance.put(url).then((response) => response.data);
}

export function putExpenseFiles(detailId: number, files: FileUploads[]) {
  const url = `/expenses/details/${detailId}/files`;
  return axiosInstance.put(url, files).then((response) => response.data);
}

/*-------------------------------Export----------------------------------------*/

export function downloadExpenseReportExport(
  company: string,
  weekEndingDate: Date | string,
  approvedOnly: boolean,
  unExportedOnly: boolean,
  token?: CancelToken
): Promise<AxiosResponse<File>> {
  const url = '/expenses/export/' + company + '?weekEndingDate=' + weekEndingDate + (approvedOnly ? '&approved=true' : '') + (unExportedOnly ? '&unexported=true' : '');

  var config: any = {
    responseType: 'blob',
  };

  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    var filename =
      response.headers['content-disposition']?.substring(
        response.headers['content-disposition']?.indexOf('=') + 1,
        response.headers['content-disposition']?.indexOf('xlsx') + 4
      ) ?? `expensesDownload_${getUTCAmericanDateString(weekEndingDate)}.xlsx`;
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    return response;
  });
}

/*-------------------------------Interfaces----------------------------------------*/

export interface ExpenseReportHeader extends CreatedUpdatedTable {
  id?: number;
  userId?: string;
  user?: User;
  periodEndingDate?: string | Date;
  managerApprovedBy?: string;
  managerApprovedDate?: string | Date;
  accountingApprovedBy?: string;
  accountingApprovedDate?: string | Date;
  lastExportedDate?: string | Date;
  details: ExpenseReportDetail[];
}

export interface ExpenseReportDetail extends CreatedUpdatedTable {
  id?: number;
  headerId?: number;
  dateOfPurchase?: Date | string;
  placeOfPurchase?: string;
  expenseType?: string;
  expenseAmount?: number;
  jobNumber?: string;
  costCode?: string;
  costCodes: CostCode[];
  comments?: string;
  isPayable?: boolean;
  header?: ExpenseReportHeader;
  files: FileUploads[];
}
